'use client';

import { ConfirmDialog } from '@components/mobile/overlays/confirm-dialog';
import {
  ResponsiveActionMenu,
  type ResponsiveAction
} from '@components/mobile/overlays/responsive-action-menu';
import { Button } from '@components/ui/button';
import { isImageContentType } from '@files/constants';
import {
  canCopyImages,
  copyImageFromUrl,
  copyText,
  fileLinkForCopy
} from '@utils/clipboard';
import { cn } from '@utils/cn';
import {
  Copy,
  Download,
  Eye,
  Link2,
  MoreHorizontal,
  Trash2
} from 'lucide-react';
import { useTranslations } from 'next-intl';
import { FC, useState } from 'react';
import { toast } from 'sonner';
import type { AttachmentView } from './AttachmentGallery';

export function downloadUrlFor(file: Pick<AttachmentView, 'url'>): string {
  return `${file.url}${file.url.includes('?') ? '&' : '?'}download=1`;
}

function triggerDownload(file: AttachmentView) {
  const anchor = document.createElement('a');
  anchor.href = downloadUrlFor(file);
  anchor.download = file.filename;
  anchor.rel = 'noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

type FileActionsMenuProps = {
  file: AttachmentView;
  canDelete?: boolean;
  /** Omit inside the viewer, where the file is already open. */
  onOpen?: () => void;
  onDelete?: (file: AttachmentView) => unknown;
  triggerClassName?: string;
  align?: 'start' | 'end';
};

/**
 * Slack-style "more actions" for one attachment: DropdownMenu on desktop,
 * ActionSheet on phones. Delete confirms through ConfirmDialog.
 */
const FileActionsMenu: FC<FileActionsMenuProps> = ({
  file,
  canDelete = false,
  onOpen,
  onDelete,
  triggerClassName,
  align = 'end'
}) => {
  const t = useTranslations('files');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const copyLink = async () => {
    if (await copyText(fileLinkForCopy(file.url))) {
      toast.success(t('link-copied'), {
        description: t('link-copied-description')
      });
    } else {
      toast.error(t('copy-failed'));
    }
  };

  const copyImage = async () => {
    if (await copyImageFromUrl(file.url, file.contentType)) {
      toast.success(t('image-copied'));
    } else {
      toast.error(t('copy-failed'));
    }
  };

  const actions: ResponsiveAction[] = [];
  if (onOpen) {
    actions.push({
      id: 'open',
      label: t('open'),
      icon: <Eye />,
      onSelect: onOpen
    });
  }
  actions.push({
    id: 'download',
    label: t('download'),
    icon: <Download />,
    href: downloadUrlFor(file),
    download: file.filename,
    onSelect: () => triggerDownload(file)
  });
  actions.push({
    id: 'copy-link',
    label: t('copy-link'),
    icon: <Link2 />,
    onSelect: () => void copyLink()
  });
  if (isImageContentType(file.contentType) && canCopyImages()) {
    actions.push({
      id: 'copy-image',
      label: t('copy-image'),
      icon: <Copy />,
      onSelect: () => void copyImage()
    });
  }
  const deleteAction: ResponsiveAction | null =
    canDelete && onDelete
      ? {
          id: 'delete',
          label: t('delete'),
          icon: <Trash2 />,
          destructive: true,
          onSelect: () => setConfirmOpen(true)
        }
      : null;

  const trigger = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={t('more-actions', { name: file.filename })}
      data-no-message-menu=""
      className={cn('shrink-0', triggerClassName)}
      onPointerDown={event => event.stopPropagation()}
    >
      <MoreHorizontal />
    </Button>
  );

  const confirm = (
    <ConfirmDialog
      open={confirmOpen}
      onOpenChange={setConfirmOpen}
      title={t('delete-title')}
      description={t('delete-description', { name: file.filename })}
      cancelLabel={t('cancel')}
      confirmLabel={t('delete')}
      onConfirm={() => {
        setConfirmOpen(false);
        void onDelete?.(file);
      }}
    />
  );

  return (
    <>
      <ResponsiveActionMenu
        trigger={trigger}
        title={file.filename}
        groups={[actions, deleteAction ? [deleteAction] : []]}
        align={align}
        contentClassName="min-w-48 rounded-xl border-border/80 bg-popover/95 p-1 shadow-none backdrop-blur-2xl"
        itemClassName="text-body-dense"
      />
      {confirm}
    </>
  );
};

export default FileActionsMenu;
